import { useEffect } from 'react';
import { useRegion } from '../hooks/useRegion';
import { buildCookieConsentConfig } from './jurisdictionConfigs';
import { initAppInsights, stopAppInsights } from '../appInsights';

/**
 * Mounts the multi-jurisdiction cookie consent banner once per page load.
 *
 * The vanilla-cookieconsent v3 library reads/writes its decision to
 * localStorage and renders the banner via a single root element appended to
 * <body>. We only call `run()` once — the library is idempotent across React
 * remounts (StrictMode double-effect, route changes), but we still guard
 * against running during SSR (vite-react-ssg pre-render pass) where
 * `window` is undefined.
 *
 * The active region comes from useRegion (hostname → RegionConfig), which
 * supplies the `privacyJurisdiction` discriminator that drives the regime-
 * specific config in `jurisdictionConfigs.ts`.
 *
 * Authority:
 *  - ADR-0037 (vanilla-cookieconsent v3 carveout)
 *  - code-handoff-fy27 § 12.3 (RegionConfig.compliance) + § 12.8 (UX direction)
 */
export function CookieConsentMount(): null {
  const region = useRegion();

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Dynamic import keeps the consent library out of the SSR bundle and
    // delays the CSS injection until the page is hydrated in the browser.
    let cancelled = false;
    void (async () => {
      const [CookieConsent] = await Promise.all([
        import('vanilla-cookieconsent'),
        import('vanilla-cookieconsent/dist/cookieconsent.css'),
      ]);
      if (cancelled) return;
      // Gate Application Insights behind the 'analytics' consent category
      // (every jurisdiction config exposes it). onConsent fires on first
      // acceptance and on later loads with stored consent; onChange on edits.
      // So analytics only runs after explicit opt-in, and is torn down on
      // withdrawal. (AI is no longer started unconditionally at boot.)
      const syncAnalytics = () => {
        if (CookieConsent.acceptedCategory('analytics')) {
          void initAppInsights();
        } else {
          stopAppInsights();
        }
      };
      CookieConsent.run({
        ...buildCookieConsentConfig(region),
        onConsent: syncAnalytics,
        onChange: syncAnalytics,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [region]);

  return null;
}
